import { z } from 'zod';

/** Default 1099-NEC reporting thresholds by tax year (USD). */
export const DEFAULT_NEC_1099_THRESHOLDS: Record<string, number> = {
  '2025': 600,
  '2026': 2000,
};

/**
 * One company pays an expense that belongs to another. At month end the
 * payer moves that month's postings out of its expense accounts into a
 * receivable from the borrower, and the borrower books the same expense
 * against a payable to the payer. Both entries carry `${referencePrefix}-YYYY-MM`,
 * which is what makes the month-end job idempotent: it posts only the
 * difference between what the month's expense accounts hold and what has
 * already been reallocated under that reference.
 */
export const intercompanyReallocationRuleSchema = z.object({
  id: z.string().min(1).max(40),
  /** Memo template; {MONTH} and {YYYY} are substituted. */
  label: z.string().min(1).max(200),
  payerCompanyId: z.string().uuid(),
  /** Expense account codes at the payer whose month's movement is moved out. */
  payerExpenseCodes: z.array(z.string().min(1)).min(1),
  /** Asset account at the payer that receives the balance ("Loans to X"). */
  payerReceivableCode: z.string().min(1),
  borrowerCompanyId: z.string().uuid(),
  /** Expense account at the borrower that takes the cost. */
  borrowerExpenseCode: z.string().min(1),
  /** Liability account at the borrower ("Due to Y"). */
  borrowerPayableCode: z.string().min(1),
  referencePrefix: z.string().regex(/^[A-Z0-9-]{2,20}$/, 'upper-case letters, digits and dashes'),
  /** IANA zone used to decide when "the last day of the month" is. */
  timeZone: z.string().min(1).default('America/Chicago'),
  isActive: z.boolean().default(true),
});
export type IntercompanyReallocationRule = z.infer<typeof intercompanyReallocationRuleSchema>;

/** App-wide configurable settings (not company-specific). */
export interface AppSettings {
  /** Tax year (e.g. "2026") → 1099-NEC Box 1 reporting threshold. */
  nec1099Thresholds: Record<string, number>;
  /** Month-end intercompany expense reallocations run by the scheduled job. */
  intercompanyReallocations: IntercompanyReallocationRule[];
}

export const updateSettingsInputSchema = z.object({
  nec1099Thresholds: z
    .record(z.string().regex(/^\d{4}$/, 'year must be YYYY'), z.number().nonnegative())
    .optional(),
  /** Replaces the whole list. */
  intercompanyReallocations: z.array(intercompanyReallocationRuleSchema).max(50).optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsInputSchema>;

/**
 * Resolve the 1099-NEC threshold for a year: exact match, else the most recent
 * configured year at or before it, else the earliest configured, else $600.
 */
export function resolveNec1099Threshold(
  thresholds: Record<string, number>,
  year: number,
): number {
  const exact = thresholds[String(year)];
  if (exact != null) return exact;

  const definedYears = Object.keys(thresholds)
    .map(Number)
    .filter((y) => !Number.isNaN(y))
    .sort((a, b) => a - b);
  if (definedYears.length === 0) return 600;

  const atOrBefore = definedYears.filter((y) => y <= year);
  const pick = atOrBefore.length ? atOrBefore[atOrBefore.length - 1]! : definedYears[0]!;
  return thresholds[String(pick)] ?? 600;
}
